// ✅ src/pages/VehicleV2/VehicleTransferList.jsx  (FULL FIXED FILE)

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaFilter,
  FaEye,
  FaSyncAlt,
  FaUndo,
  FaPlus,
  FaArrowLeft,
  FaFileExport,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleTransferListing";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const normalizeSelect = (v, fallback = "Select") => {
  const s = clean(v);
  return s || fallback;
};

const defaultFilters = {
  filterType: "own", // own / spares
  searchBy: "branch", // branch / location
  branchName: "",
  fromDate: "",
  toDate: "",
  month: "Select",
  year: "Select",
  vehicleMainType: "Select",
  vehicleCode: "",
  vehicleRegNo: "",
  fromLocation: "Select",
  toLocation: "Select",
  store: "Select",
  vehicleType: "", // UI only
};

const VehicleTransferList = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session storage pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [months, setMonths] = useState(["Select"]);
  const [years, setYears] = useState([{ id: "Select", name: "Select" }]);

  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  const isBusy = pageLoading || dropdownLoading || gridLoading || exporting;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const makeSelectOptions = useCallback((arr, idKey, nameKey) => {
    const items = Array.isArray(arr) ? arr : [];
    return [
      { [idKey]: "Select", [nameKey]: "Select" },
      ...items,
    ];
  }, []);

  const buildListParams = useCallback(
    (activeFilters) => ({
      filterType: activeFilters.filterType,
      searchBy: activeFilters.searchBy,
      branchName: activeFilters.branchName,
      fromDate: activeFilters.fromDate,
      toDate: activeFilters.toDate,
      month: normalizeSelect(activeFilters.month),
      year: normalizeSelect(activeFilters.year),
      vehicleMainType: normalizeSelect(activeFilters.vehicleMainType),
      vehicleCode: activeFilters.vehicleCode,
      vehicleRegNo: activeFilters.vehicleRegNo,
      fromLocation: normalizeSelect(activeFilters.fromLocation),
      toLocation: normalizeSelect(activeFilters.toLocation),
      store: normalizeSelect(activeFilters.store),
      loginUserId,
      sessionBranchId: branchId,
      branchType,
    }),
    [loginUserId, branchId, branchType]
  );

  /* =========================================================
     API LOADERS
  ========================================================= */
  const loadDropdowns = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load dropdowns");
      }

      setBranches(data.branches || []);
      setLocations(
        makeSelectOptions(
          data.locations || [],
          "fld_Worklocationid",
          "fld_Worklocationname"
        )
      );
      setStores(
        makeSelectOptions(data.stores || [], "fld_storeid", "fld_storename")
      );
      setVehicleMainTypes(
        makeSelectOptions(
          data.vehicleMainTypes || [],
          "fld_vehicletypeindex",
          "fld_vehicletypename"
        )
      );

      setMonths(
        Array.isArray(data.months) && data.months.length > 0
          ? data.months.map(String)
          : ["Select"]
      );

      setYears(
        Array.isArray(data.years) && data.years.length > 0
          ? data.years
          : [{ id: "Select", name: "Select" }]
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load dropdowns"
      );
    } finally {
      setDropdownLoading(false);
    }
  }, [branchId, commonHeaders, makeSelectOptions]);

  const loadList = useCallback(
    async ({ silent = false, activeFilters = defaultFilters } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(activeFilters),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          if (!silent) {
            toast.warn(data?.message || "No data found");
          }
          return;
        }

        setRows(data.list || []);

        if (!silent) {
          toast.success(`Found ${data.count ?? data.list?.length ?? 0} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, buildListParams, commonHeaders, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      await loadDropdowns();
      await loadList({ silent: true, activeFilters: defaultFilters });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback((e) => {
    const { name, value, type } = e.target;
    setFilters((p) => ({
      ...p,
      [name]: type === "radio" ? value : value,
    }));
  }, []);

  const handleSearch = useCallback(async () => {
    await loadList({ silent: false, activeFilters: filters });
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = {
      ...defaultFilters,
      filterType: filters.filterType,
    };

    setFilters(reset);
    await loadList({
      silent: false,
      activeFilters: reset,
    });
  }, [filters.filterType, loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const handleClearFilters = useCallback(() => {
    setFilters((p) => ({
      ...p,
      branchName: "",
      fromDate: "",
      toDate: "",
      month: "Select",
      year: "Select",
      vehicleMainType: "Select",
      vehicleCode: "",
      vehicleRegNo: "",
      fromLocation: "Select",
      toLocation: "Select",
      store: "Select",
      vehicleType: "",
      searchBy: "branch",
    }));
    toast.info("Filters cleared");
  }, []);

  const exportExcel = useCallback(async () => {
    try {
      setExporting(true);
      toast.info("Export feature can be connected to backend /export endpoint");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, []);

  const handleAddNew = useCallback(() => {
    navigate("/vehicle/VehicleTransferEntry");
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const recordsCount = useMemo(() => rows.length, [rows]);

  const tableColumns = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0] || {}).slice(0, 12);
  }, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Entry Listing
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {filters.filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={exportExcel}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? "bg-green-700 hover:bg-green-600 text-white" : "bg-green-600 hover:bg-green-700 text-white"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                {exporting ? "Exporting..." : "Export"}
              </button>

              <button
                onClick={handleAddNew}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? "bg-blue-700 hover:bg-blue-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {/* Vehicle Type / Top Selection */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="own"
                    checked={filters.filterType === "own"}
                    onChange={onFilterChange}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="spares"
                    checked={filters.filterType === "spares"}
                    onChange={onFilterChange}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Spares & Catalogue
                  </span>
                </label>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Branch */}
                  <div>
                    <label className={labelClass}>Branch Name</label>
                    <select
                      name="branchName"
                      value={filters.branchName}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="">Select</option>
                      {branches.map((b) => (
                        <option key={b.fld_Branchid} value={String(b.fld_Branchid)}>
                          {b.fld_BranchName || `Branch ${b.fld_Branchid}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Main Type */}
                  <div>
                    <label className={labelClass}>Vehicle Main Type</label>
                    <select
                      name="vehicleMainType"
                      value={filters.vehicleMainType}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {vehicleMainTypes.map((x, idx) => (
                        <option
                          key={idx}
                          value={String(x.fld_vehicletypeindex ?? "Select")}
                        >
                          {String(x.fld_vehicletypename ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* From Date */}
                  <div>
                    <label className={labelClass}>From Date</label>
                    <input
                      type="date"
                      name="fromDate"
                      value={filters.fromDate}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  {/* To Date */}
                  <div>
                    <label className={labelClass}>To Date</label>
                    <input
                      type="date"
                      name="toDate"
                      value={filters.toDate}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  {/* Month */}
                  <div>
                    <label className={labelClass}>Month</label>
                    <select
                      name="month"
                      value={filters.month}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {months.map((m, idx) => (
                        <option key={idx} value={String(m)}>
                          {String(m)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Year */}
                  <div>
                    <label className={labelClass}>Year</label>
                    <select
                      name="year"
                      value={filters.year}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {years.map((y, idx) => (
                        <option key={idx} value={String(y?.id ?? y)}>
                          {String(y?.name ?? y)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Reg No */}
                  <div>
                    <label className={labelClass}>Vehicle Registration Number</label>
                    <input
                      type="text"
                      name="vehicleRegNo"
                      value={filters.vehicleRegNo}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="Enter vehicle registration number"
                      disabled={isBusy}
                    />
                  </div>

                  {/* Vehicle Code */}
                  <div>
                    <label className={labelClass}>Vehicle Code</label>
                    <input
                      type="text"
                      name="vehicleCode"
                      value={filters.vehicleCode}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="Enter vehicle code"
                      disabled={isBusy}
                    />
                  </div>

                  {/* From Location */}
                  <div>
                    <label className={labelClass}>From Location</label>
                    <select
                      name="fromLocation"
                      value={filters.fromLocation}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {locations.map((l, idx) => (
                        <option
                          key={idx}
                          value={String(l.fld_Worklocationid ?? "Select")}
                        >
                          {String(l.fld_Worklocationname ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* To Location */}
                  <div>
                    <label className={labelClass}>To Location</label>
                    <select
                      name="toLocation"
                      value={filters.toLocation}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {locations.map((l, idx) => (
                        <option
                          key={idx}
                          value={String(l.fld_Worklocationid ?? "Select")}
                        >
                          {String(l.fld_Worklocationname ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Store */}
                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      name="store"
                      value={filters.store}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {stores.map((s, idx) => (
                        <option key={idx} value={String(s.fld_storeid ?? "Select")}>
                          {String(s.fld_storename ?? "Select")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Type (UI only) */}
                  <div>
                    <label className={labelClass}>Vehicle Type (optional)</label>
                    <input
                      type="text"
                      name="vehicleType"
                      value={filters.vehicleType}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="(optional)"
                      disabled={isBusy}
                    />
                  </div>
                </div>

                {/* Search By */}
                <div className="mt-6">
                  <label className={labelClass}>Search By</label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="searchBy"
                        value="branch"
                        checked={filters.searchBy === "branch"}
                        onChange={onFilterChange}
                        className="form-radio text-blue-600"
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Branch
                      </span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="searchBy"
                        value="location"
                        checked={filters.searchBy === "location"}
                        onChange={onFilterChange}
                        className="form-radio text-blue-600"
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Location
                      </span>
                    </label>
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Transfer List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {filters.filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{rows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    {tableColumns.length === 0 ? (
                      <th className="px-4 py-3 text-center">Data</th>
                    ) : (
                      tableColumns.map((c) => (
                        <th key={c} className="px-4 py-3 text-center">
                          {c}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableColumns.length || 1}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr
                        key={idx}
                        className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                      >
                        {tableColumns.map((col) => (
                          <td key={col} className="px-4 py-3 text-center whitespace-nowrap">
                            {String(row?.[col] ?? "-")}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : exporting
                  ? "Exporting..."
                  : "Loading..."}
              </div>
            )}
          </div>

          {/* Back */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferList;